// Keywords a warscroll gets automatically from its other fields and its army:
// the army keyword, Grand Alliance, unit type, and the Warmaster/Legends/Ward
// flags. In the studio these render as locked chips (not editable), so they're
// NOT kept in the authored unit's stored `keywords` — that would let them drift
// or duplicate. Instead they're derived, and baked into `keywords` only at the
// export boundary (withDerivedUnitKeywords) so consumers like the frontend see
// the full keyword line; the studio strips them again on load
// (stripDerivedUnitKeywords) so its stored set stays author-only. This mirrors
// how authored battleplan names are stripped on export and re-derived on load.
import { UnitType, unitTypeToString } from '../models/IUnit.js';
import { GrandAlliance } from '../models/IAlliance.js';
export function derivedUnitKeywords(unit, ctx = {}) {
    const out = [];
    if (ctx.armyKeyword?.trim())
        out.push(ctx.armyKeyword.trim().toUpperCase());
    if (ctx.alliance && ctx.alliance !== GrandAlliance.UNKNOWN)
        out.push(ctx.alliance);
    if (unit.type !== UnitType.Unknown)
        out.push(unitTypeToString(unit).toUpperCase());
    if (unit.isWarmaster)
        out.push('WARMASTER');
    if (unit.legends)
        out.push('LEGENDS');
    if (unit.ward?.trim())
        out.push(`WARD (${unit.ward.trim()})`);
    return out;
}
// The derive context for an army: the keyword defaults to the army name.
function armyContext(army) {
    return { armyKeyword: army.keyword?.trim() || army.name, alliance: army.alliance };
}
// Bake the derived keywords into every unit's `keywords` (derived first, then
// the author's own, de-duped case-insensitively). Pure — returns a new army.
export function withDerivedUnitKeywords(army) {
    if (!army.units?.length)
        return army;
    const ctx = armyContext(army);
    return {
        ...army,
        units: army.units.map((unit) => {
            const seen = new Set();
            const keywords = [];
            for (const kw of [...derivedUnitKeywords(unit, ctx), ...unit.keywords]) {
                const key = kw.toUpperCase();
                if (!seen.has(key)) {
                    seen.add(key);
                    keywords.push(kw);
                }
            }
            return { ...unit, keywords };
        }),
    };
}
// Remove the derived keywords from every unit's `keywords`, leaving only the
// author's own. Inverse of withDerivedUnitKeywords; idempotent on already-clean
// armies. Pure — returns a new army.
export function stripDerivedUnitKeywords(army) {
    if (!army.units?.length)
        return army;
    const ctx = armyContext(army);
    return {
        ...army,
        units: army.units.map((unit) => {
            const derived = new Set(derivedUnitKeywords(unit, ctx).map((k) => k.toUpperCase()));
            return { ...unit, keywords: unit.keywords.filter((k) => !derived.has(k.toUpperCase())) };
        }),
    };
}
// Regiments of Renown are global content and have no owning army at export time.
// Bake only the intrinsic unit-derived keywords (type, ward, warmaster, legends).
export function withDerivedRegimentUnitKeywords(regiment) {
    if (!regiment.units?.length)
        return regiment;
    return {
        ...regiment,
        units: regiment.units.map((entry) => {
            const seen = new Set();
            const keywords = [];
            for (const kw of [...derivedUnitKeywords(entry.unit), ...entry.unit.keywords]) {
                const key = kw.toUpperCase();
                if (!seen.has(key)) {
                    seen.add(key);
                    keywords.push(kw);
                }
            }
            return { ...entry, unit: { ...entry.unit, keywords } };
        }),
    };
}
export function stripDerivedRegimentUnitKeywords(regiment) {
    if (!regiment.units?.length)
        return regiment;
    return {
        ...regiment,
        units: regiment.units.map((entry) => {
            const derived = new Set(derivedUnitKeywords(entry.unit).map((k) => k.toUpperCase()));
            return {
                ...entry,
                unit: {
                    ...entry.unit,
                    keywords: entry.unit.keywords.filter((k) => !derived.has(k.toUpperCase())),
                },
            };
        }),
    };
}
